import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TranslateService, TranslationObject } from '@ngx-translate/core';
import { catchError, firstValueFrom, from, Observable, of } from 'rxjs';

import { LocalizedValue } from '@celum/core/model';

/**
 * Service to load and inject translations from remote sources into the main TranslateService.
 *
 * ⚠️ This service assumes that the main applications translations are already loaded!
 */
@Injectable({ providedIn: 'root' })
export class InjectTranslationsService {
  private translateService = inject(TranslateService);
  private httpClient = inject(HttpClient);

  public loadTranslations(config: { basePath: string; translations: LocalizedValue; remoteName: string; useBasicFetch?: boolean }): Promise<any> {
    if (!config.translations) {
      return Promise.resolve();
    }

    const localePromises = Object.keys(config.translations).map(async locale => {
      const translationUrl = config.translations[locale];
      const computedUrl = config.basePath + translationUrl;

      return firstValueFrom(
        this.fetchTranslationFile(computedUrl, config.useBasicFetch).pipe(
          catchError(err => {
            console.error(`InjectTranslationsService: Could not load translations for locale '${locale}' for remote '${config.remoteName}'.`, err);
            return of(null);
          })
        )
      ).then(data => data && this.injectTranslations(locale, data, config.remoteName));
    });

    return Promise.all(localePromises);
  }

  private fetchTranslationFile(computedUrl: string, useBasicFetch = false): Observable<TranslationObject> {
    if (useBasicFetch) {
      return from(fetch(computedUrl).then(response => response.json()));
    }

    return this.httpClient.get<TranslationObject>(computedUrl);
  }

  private injectTranslations(locale: string, translations: TranslationObject, remoteName: string): void {
    try {
      this.translateService.setTranslation(locale, translations, true);
    } catch (err) {
      console.error(`InjectTranslationsService: Could not apply translations for locale ${locale} for embed ${remoteName}.`, err);
    }
  }
}
